import styled, { css } from 'styled-components';
import { Link } from 'react-router-dom';

interface NavBarContainerProps {
    navWidth?: number;
}

export const NavBar = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: 100%;
    max-width: 100vw;
    min-height: 4rem;
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    border-bottom: 1px solid #f3f4f6;
    z-index: 50;
    transition: all 0.3s ease;
    overflow: visible;

    /* Fallback for browsers without backdrop-filter support */
    @supports not (backdrop-filter: blur(12px)) {
        background: rgba(255, 255, 255, 0.98);
    }

    @media (min-width: ${({ theme }) => theme.screenSizes.tablet}) {
        min-height: 5rem;
    }
`;

export const NavBarContainer = styled.div<NavBarContainerProps>`
    display: flex;
    width: 100%;
    max-width: 100%;
    justify-content: space-between;
    align-items: center;
    gap: 0.5rem;
    padding: 0 1rem;
    margin: 0 auto;
    min-height: 4rem;
    box-sizing: border-box;

    @media (min-width: ${({ theme }) => theme.screenSizes.tablet}) {
        gap: 1rem;
        padding: 0 1.5rem;
        min-height: 5rem;
        max-width: ${props => props.navWidth ? `${props.navWidth}vw` : '100%'};
    }

    ${props => props.navWidth && css`
        @media (max-width: ${({ theme }) => theme.screenSizes.laptopL}) {
            max-width: 75vw;
        };
        
        @media (max-width: ${({ theme }) => theme.screenSizes.laptop}) {
            max-width: 85vw;
        };
        
        @media (max-width: ${({ theme }) => theme.screenSizes.tablet}) {
            max-width: 95vw;
        };
        
        @media (max-width: ${({ theme }) => theme.screenSizes.mobileL}) {
            max-width: 100vw;
        };
    `}
`;

export const LogoContainer = styled(Link)`
    border-radius: 5px;
    height: 36px;
    min-width: 70px;
    align-items: center;
    justify-content: center;
    align-content: center;
    display: flex;

    @media (min-width: ${({ theme }) => theme.screenSizes.tablet}) {
        height: 40px;
        min-width: 100px;
    }
`;

export const TitleMain = styled.p`
    font-family: 'CocomatProBold', sans-serif;
    font-size: 1.5rem;
    color: #000;
    text-align: center;
    margin: 0;
    padding: 0;
    font-weight: 1000;
    letter-spacing: -0.05em;
    text-transform: uppercase;

    @media (min-width: ${({ theme }) => theme.screenSizes.tablet}) {
        font-size: 1.875rem;
    }
`;

export const NavRight = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 1.5rem;

    & a {
        text-decoration: none;
    }

    @media (max-width: ${({ theme }) => theme.screenSizes.tablet}) {
        display: none;
    }
`;

export const Btn = styled.button`
    cursor: pointer;
    display: inline-block;
    border: 1px #000 solid;
    border-radius: 50px;
    padding: 7px 16px;
    line-height: 1.2;
    text-align: center;
    text-decoration: none;

    &:hover {
        opacity: 0.7;
    }
`;

export const BtnDarkOutline = styled(Btn)`
    border-color: #000;
    color: #000;
`;

export const BtnDark = styled(Btn)`
    background-color: #000;
    color: #fff;
`;

export const FlexRow = styled.div`
    display: flex;
    align-items: center;

    // LEFT column (MORA logo)
    &:first-child {
        flex-shrink: 0;
        min-width: auto;
    }

    // CENTER column (SearchBarSmall) - allow flexibility for category pills
    &:nth-child(2) {
        flex: 1;
        justify-content: center;
        min-width: 0;
        display: none;

        @media (min-width: ${({ theme }) => theme.screenSizes.tablet}) {
            display: flex;
        }
    }

    // RIGHT column (buttons)
    &:last-child {
        flex-shrink: 0;
        justify-content: flex-end;
    }

    @media (min-width: ${({ theme }) => theme.screenSizes.tablet}) {
        // LEFT column (MORA logo)
        &:first-child {
            min-width: 100px;
        }
    }
`;

export const LoginButton = styled.button`
    background: transparent;
    border: none;
    color: #374151;
    font-size: 0.875rem;
    font-weight: 600;
    padding: 0.5rem;
    cursor: pointer;
    transition: color 0.2s;

    &:hover {
        color: #000;
    }
`;

export const UserMenu = styled.div`
    display: flex;
    align-items: center;
    gap: 1rem;
`;

export const UserGreeting = styled.span`
    color: #000;
    font-size: 0.875rem;
    font-weight: 500;
`;

export const LogoutButton = styled.button`
    background: transparent;
    border: 1px solid #000;
    border-radius: 0.5rem;
    padding: 0.5rem 1rem;
    color: #000;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
        background: #000;
        color: #fff;
    }
`;

export const BusinessButton = styled.button`
    background: #000;
    color: #fff;
    font-size: 0.875rem;
    font-weight: 600;
    padding: 0.625rem 1.25rem;
    border: none;
    border-radius: 9999px;
    cursor: pointer;
    transition: all 0.2s;
    white-space: nowrap;
    flex-shrink: 0;

    &:hover {
        background: #1f2937;
    }

    @media (max-width: 900px) {
        padding: 0.5rem 1rem;
        font-size: 0.8125rem;
    }
`;

export const MobileMenuButton = styled.button`
    display: none;
    background: transparent;
    border: none;
    color: #374151;
    cursor: pointer;
    padding: 0.5rem;

    &:hover {
        color: #000;
    }

    @media (max-width: ${({ theme }) => theme.screenSizes.tablet}) {
        display: flex;
        align-items: center;
        justify-content: center;
    }
`;

export const MobileMenu = styled.div`
    display: none;
    position: absolute;
    top: 4rem;
    left: 0;
    width: 100%;
    background: #fff;
    border-bottom: 1px solid #f3f4f6;
    padding: 0.5rem 1rem 1.5rem;
    z-index: 999;
    animation: slideDown 0.2s ease-out;
    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1);

    @keyframes slideDown {
        from {
            opacity: 0;
            transform: translateY(-10px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }

    @media (max-width: ${({ theme }) => theme.screenSizes.tablet}) {
        display: block;
    }
`;

export const MobileMenuItem = styled.a`
    display: block;
    padding: 0.75rem;
    font-size: 1rem;
    font-weight: 500;
    color: #111827;
    text-decoration: none;
    border-radius: 0.375rem;
    transition: background 0.2s;

    &:hover {
        background: #f9fafb;
    }
`;

// Profile Dropdown (logged-in state)
export const ProfileContainer = styled.div`
    position: relative;
`;

export const ProfileButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    border: 1px solid #e0e0e0;
    background: #ffffff;
    color: #222222;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
        background: #f5f5f5;
        border-color: #222222;
    }
`;

export const ProfileDropdown = styled.div`
    position: absolute;
    top: calc(100% + 8px);
    right: 0;
    min-width: 200px;
    background: #ffffff;
    border: 1px solid #e0e0e0;
    border-radius: 12px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    padding: 8px 0;
    z-index: 100;
    animation: fadeIn 0.15s ease-out;

    @keyframes fadeIn {
        from { opacity: 0; transform: translateY(-8px); }
        to { opacity: 1; transform: translateY(0); }
    }
`;

export const DropdownItem = styled.button`
    display: block;
    width: 100%;
    padding: 12px 16px;
    text-align: left;
    background: transparent;
    border: none;
    font-size: 14px;
    font-weight: 500;
    color: #222222;
    cursor: pointer;
    transition: background 0.15s;

    &:hover {
        background: #f5f5f5;
    }
`;

export const DropdownDivider = styled.div`
    height: 1px;
    background: #e0e0e0;
    margin: 8px 0;
`;